// =====================================================================
// ImpressionFormModal.jsx - Modal create/edit job d'impression (M-003 Faza 2)
// =====================================================================
// Pattern: PrelevementFormModal.jsx ca referință de stil.
// Props:
//   - jobId: null = create, string = edit existing
//   - onClose: () => void
//   - onSaved: () => void
//
// Permission logic:
//   - create.own / create.all -> POST nou
//   - update.own + same-day OR update.all -> edit
//   - delete.all -> buton Supprimer vizibil in mod edit (ADMIN+)
//   - Daca nu poate edita, modal in read-only.
//
// FK STRICT:
//   - clientId: dropdown PrintingClient (sortat alfabetic by label)
//   - taskTemplateId: dropdown PrintingTaskTemplate
//     (sortat by usageCount DESC -> cele mai populare primele)
//
// IMPORTANT: backend foloseste PATCH pentru update (NU PUT), POST pentru
// /invoice toggle (Greseala #12 evitata preventiv).
// =====================================================================

import { useState, useEffect } from 'react';
import {
  printingJobs as jobsApi,
  printingClients as clientsApi,
  printingTaskTemplates as templatesApi,
} from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';

const today = () => new Date().toISOString().slice(0, 10);

const MAX_COMMENT = 500;

export default function ImpressionFormModal({ jobId, onClose, onSaved }) {
  const { user, can } = useAuth();
  const isEdit = !!jobId;

  // Form fields
  const [date, setDate]                 = useState(today());
  const [clientId, setClientId]         = useState('');
  const [taskTemplateId, setTplId]      = useState('');
  const [pages, setPages]               = useState(1);
  const [couleur, setCouleur]           = useState('BW');
  const [paperType, setPaperType]       = useState('A4');
  const [comment, setComment]           = useState('');

  // Read-only metadata
  const [ref, setRef]                   = useState('');
  const [createdByName, setCreator]     = useState(user?.name || '');
  const [invoiced, setInvoiced]         = useState(false);
  const [invoicedAt, setInvoicedAt]     = useState(null);
  const [invoicedByName, setInvoicedBy] = useState('');
  const [createdAt, setCreatedAt]       = useState(null);
  const [updatedAt, setUpdatedAt]       = useState(null);

  // Catalog dropdowns
  const [clients, setClients]     = useState([]);
  const [templates, setTemplates] = useState([]);

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [readOnly, setReadOnly] = useState(false);

  const canUpdateAll = can('printing_jobs.update.all');
  const canUpdateOwn = can('printing_jobs.update.own');
  const canDelete    = can('printing_jobs.delete.all');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Catalog: client sortat alfabetic by label, template sortat
        // by usageCount DESC (cele mai populare primele in dropdown)
        const [clList, tplList] = await Promise.all([
          clientsApi.list({ isActive: true }),
          templatesApi.list({ isActive: true }),
        ]);
        if (cancelled) return;
        const clArr = (Array.isArray(clList) ? clList : (clList.items || []))
          .slice()
          .sort((a, b) => (a.label || '').localeCompare(b.label || '', 'fr'));
        const tplArr = (Array.isArray(tplList) ? tplList : (tplList.items || []))
          .slice()
          .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0));
        setClients(clArr);
        setTemplates(tplArr);

        if (isEdit) {
          const job = await jobsApi.get(jobId);
          if (cancelled) return;
          setRef(job.ref || '');
          setDate(job.date ? job.date.slice(0, 10) : today());
          setClientId(job.clientId || '');
          setTplId(job.taskTemplateId || '');
          setPages(job.pages ?? 1);
          setCouleur(job.couleur || 'BW');
          setPaperType(job.paperType || 'A4');
          setComment(job.comment || '');
          setCreator(job.createdBy?.name || '');
          setInvoiced(!!job.invoiced);
          setInvoicedAt(job.invoicedAt || null);
          setInvoicedBy(job.invoicedBy?.name || '');
          setCreatedAt(job.createdAt || null);
          setUpdatedAt(job.updatedAt || null);

          const isOwn = job.createdByUserId === user?.id;
          const isSameDay = (job.date || '').slice(0, 10) === today();
          const canEdit = canUpdateAll || (isOwn && isSameDay && canUpdateOwn);
          setReadOnly(!canEdit);
        } else {
          try {
            const { ref: nextRef } = await jobsApi.nextRef();
            if (!cancelled) setRef(nextRef);
          } catch (_e) { /* nu blochez form */ }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[ImpressionFormModal] load error', err);
          setError(err.response?.data?.message || 'Erreur lors du chargement');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [jobId, isEdit, user?.id, canUpdateAll, canUpdateOwn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (!clientId || !taskTemplateId || !pages || Number(pages) <= 0) {
      setError('Veuillez remplir tous les champs obligatoires (client, tâche, pages > 0).');
      return;
    }
    if (!Number.isInteger(Number(pages))) {
      setError('Pages doit être un nombre entier.');
      return;
    }
    if ((comment || '').length > MAX_COMMENT) {
      setError(`Commentaire trop long (max ${MAX_COMMENT} caractères).`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        date,
        clientId,
        taskTemplateId,
        pages: Number(pages),
        couleur,
        paperType,
        comment: comment.trim() || undefined,
      };
      if (isEdit) {
        await jobsApi.update(jobId, payload);
      } else {
        await jobsApi.create(payload);
      }
      onSaved();
    } catch (err) {
      console.error('[ImpressionFormModal] save error', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !isEdit) return;
    if (!confirm(`Supprimer le job ${ref} ?\n\nCette action est irréversible.`)) return;
    setSaving(true);
    setError(null);
    try {
      await jobsApi.remove(jobId);
      onSaved();
    } catch (err) {
      console.error('[ImpressionFormModal] delete error', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={backdropStyle} onClick={onClose}>
        <div onClick={e => e.stopPropagation()} style={{ ...formStyle, padding: 40, textAlign: 'center' }}>
          Chargement...
        </div>
      </div>
    );
  }

  return (
    <div style={backdropStyle} onClick={onClose}>
      <form className="modal-mobile-fullscreen" onClick={e => e.stopPropagation()} onSubmit={handleSubmit} style={formStyle}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 20px', borderBottom: '1px solid #e2e8f0',
        }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 500 }}>
            {isEdit ? (readOnly ? 'Détails du job' : 'Modifier le job') : 'Nouveau job d\'impression'}
          </h2>
          <div style={{
            fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12,
            color: '#64748b', padding: '2px 8px',
            background: '#f1f5f9', borderRadius: 3,
          }}>
            {ref || '(auto)'}
          </div>
        </div>

        {readOnly && (
          <div style={{
            padding: '8px 20px', background: '#fef3c7', color: '#78350f',
            fontSize: 12, borderBottom: '1px solid #fbbf24',
          }}>
            Lecture seule — vous n'avez pas la permission de modifier ce job.
          </div>
        )}

        <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <label style={labelStyle}>
            <span style={labelTextStyle}>Date *</span>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
                   disabled={readOnly || saving} required style={inputStyle} />
          </label>

          <label style={labelStyle}>
            <span style={labelTextStyle}>Client *</span>
            <select value={clientId} onChange={e => setClientId(e.target.value)}
                    disabled={readOnly || saving} required style={inputStyle}>
              <option value="">— Sélectionner —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.code} — {c.label}</option>
              ))}
            </select>
          </label>

          <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
            <span style={labelTextStyle}>Tâche * <span style={{ color: '#94a3b8', fontWeight: 400 }}>(triées par popularité)</span></span>
            <select value={taskTemplateId} onChange={e => setTplId(e.target.value)}
                    disabled={readOnly || saving} required style={inputStyle}>
              <option value="">— Sélectionner —</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.label}{t.usageCount > 0 ? ` · ${t.usageCount}×` : ''}
                </option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            <span style={labelTextStyle}>Pages *</span>
            <input type="number" min={1} step={1} value={pages}
                   onChange={e => setPages(e.target.value)}
                   disabled={readOnly || saving} required style={inputStyle} />
          </label>

          <div style={labelStyle}>
            <span style={labelTextStyle}>Couleur *</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <RadioBtn label="N&B" value="BW" current={couleur} onChange={setCouleur} disabled={readOnly || saving} />
              <RadioBtn label="Couleur" value="COLOR" current={couleur} onChange={setCouleur} disabled={readOnly || saving} />
            </div>
          </div>

          <div style={labelStyle}>
            <span style={labelTextStyle}>Type de papier *</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <RadioBtn label="A4" value="A4" current={paperType} onChange={setPaperType} disabled={readOnly || saving} />
              <RadioBtn label="A3" value="A3" current={paperType} onChange={setPaperType} disabled={readOnly || saving} />
            </div>
          </div>

          <label style={labelStyle}>
            <span style={labelTextStyle}>Créé par</span>
            <input type="text" value={createdByName} readOnly
                   style={{ ...inputStyle, opacity: 0.7, background: '#f8fafc' }} />
          </label>

          <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
            <span style={labelTextStyle}>
              Commentaire (optionnel) <span style={{ color: '#94a3b8', fontWeight: 400 }}>
                {comment.length} / {MAX_COMMENT}
              </span>
            </span>
            <textarea value={comment} onChange={e => setComment(e.target.value.slice(0, MAX_COMMENT))}
                      disabled={readOnly || saving} rows={3}
                      maxLength={MAX_COMMENT}
                      style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
          </label>

          {isEdit && (
            <div style={{
              gridColumn: '1 / -1', padding: 10,
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 4, fontSize: 11, color: '#64748b',
            }}>
              <strong>Statut facturation:</strong>{' '}
              {invoiced
                ? `✓ Facturé${invoicedAt ? ' le ' + new Date(invoicedAt).toLocaleString('fr-BE') : ''}${invoicedByName ? ' par ' + invoicedByName : ''}`
                : 'Non facturé'} &nbsp;|&nbsp;
              <strong>Créé:</strong> {createdAt ? new Date(createdAt).toLocaleString('fr-BE') : '—'} &nbsp;|&nbsp;
              <strong>Modifié:</strong> {updatedAt ? new Date(updatedAt).toLocaleString('fr-BE') : '—'}
            </div>
          )}

          {error && (
            <div style={{
              gridColumn: '1 / -1', padding: 10,
              background: '#fee2e2', color: '#991b1b',
              border: '1px solid #fca5a5', borderRadius: 4, fontSize: 13,
            }}>{error}</div>
          )}
        </div>

        <div style={{
          padding: '12px 20px', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        }}>
          <div>
            {isEdit && canDelete && !readOnly && (
              <button type="button" onClick={handleDelete} disabled={saving}
                      style={{
                        padding: '8px 14px', background: 'white', color: '#991b1b',
                        border: '1px solid #fca5a5', borderRadius: 4, fontSize: 13,
                        cursor: saving ? 'not-allowed' : 'pointer',
                      }}>
                Supprimer
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} disabled={saving}
                    style={{
                      padding: '8px 16px', background: 'white', color: '#1e293b',
                      border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13,
                      cursor: saving ? 'not-allowed' : 'pointer',
                    }}>
              {readOnly ? 'Fermer' : 'Annuler'}
            </button>
            {!readOnly && (
              <button type="submit" disabled={saving}
                      style={{
                        padding: '8px 16px', background: '#2563eb', color: 'white',
                        border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 500,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        opacity: saving ? 0.6 : 1,
                      }}>
                {saving ? '…' : 'Enregistrer'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

function RadioBtn({ label, value, current, onChange, disabled }) {
  const active = current === value;
  return (
    <button type="button"
            onClick={() => !disabled && onChange(value)}
            disabled={disabled}
            style={{
              flex: 1, padding: '8px 12px', fontSize: 13, fontWeight: active ? 500 : 400,
              border: '1px solid',
              borderColor: active ? '#2563eb' : '#d1d5db',
              background: active ? '#eff6ff' : 'white',
              color: active ? '#1d4ed8' : '#475569',
              borderRadius: 4,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}>
      {label}
    </button>
  );
}

const backdropStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '60px 20px 20px', zIndex: 50, overflow: 'auto',
};
const formStyle = {
  background: 'white', borderRadius: 8, width: '100%', maxWidth: 580,
  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
};
const labelStyle = { display: 'flex', flexDirection: 'column' };
const labelTextStyle = {
  fontSize: 12, color: '#475569', marginBottom: 4, fontWeight: 500,
};
const inputStyle = {
  width: '100%', padding: 8, border: '1px solid #d1d5db',
  borderRadius: 4, fontSize: 13, boxSizing: 'border-box',
};
